import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

type OrderStatus = "pending" | "paid" | "cancelled";
type ShippingStatus = "payment_pending" | "preparing" | "shipped" | "delivered" | "cancelled";

type OrderDetailResponse = {
  ok: true;
  item: {
    id: string;
    createdAt: number;
    status: OrderStatus;
    paidAt?: number;
    totalTry: number;
    customer: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      identityNumber?: string;
      addressLine1?: string;
      city?: string;
      district?: string;
      postcode?: string;
      billingAddress?: {
        addressLine1: string;
        city: string;
        district?: string;
        postcode?: string;
      };
      shippingAddress?: {
        addressLine1: string;
        city: string;
        district?: string;
        postcode?: string;
      };
    };
    items: Array<
      | { kind: "package"; packageKey: string; qty: number; unitPriceTry: number; title: string; sku: null; lineTotalTry: number }
      | { kind: "product"; productId: string; qty: number; unitPriceTry: number; title: string; sku: string | null; lineTotalTry: number }
    >;
    shipping?: {
      status: ShippingStatus;
      trackingNumber?: string;
      carrier?: string;
      note?: string;
      updatedAt: number;
    };
    payment?: {
      lastStatus?: string;
      lastError?: string;
      paymentPageUrl?: string;
      lastCheckedAt?: number;
    };
  };
  requests: Array<{
    id: string;
    orderId: string;
    status: "created" | "processing" | "sent" | "failed";
    packageKey: string;
    email: string;
    createdAt: number;
  }>;
};

async function fetchOrderDetail(id: string): Promise<OrderDetailResponse> {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Sipariş detayı alınamadı.");
  return json as OrderDetailResponse;
}

async function markPaid(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/paid`, { method: "POST" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Güncellenemedi.");
  return json as { ok: true };
}

async function cancelOrder(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "İptal edilemedi.");
  return json as { ok: true };
}

async function deleteOrder(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/delete`, { method: "POST" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Sipariş silinemedi.");
  return json as { ok: true };
}

async function updateShipping(input: { id: string; status: ShippingStatus; trackingNumber?: string; carrier?: string; note?: string }) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(input.id)}/shipping`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Kargo durumu güncellenemedi.");
  return json as { ok: true };
}

function formatDate(seconds?: number) {
  if (!seconds) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(seconds * 1000));
  } catch {
    return "—";
  }
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function formatAddress(address?: { addressLine1?: string; city?: string; district?: string; postcode?: string } | null) {
  if (!address) return "—";
  return [address.addressLine1, [address.district, address.city].filter(Boolean).join(" / "), address.postcode].filter(Boolean).join(" • ") || "—";
}

export default function AdminNumerolojiOrderDetail() {
  const params = useParams();
  const id = String(params.id ?? "").trim();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const detailQuery = useQuery({
    queryKey: ["admin-numeroloji-order-detail", id],
    queryFn: () => fetchOrderDetail(id),
    enabled: configured && authenticated && Boolean(id),
    staleTime: 5_000,
  });

  const item = detailQuery.data?.item;
  const hasPhysicalItem = (item?.items ?? []).some((entry) => entry.kind === "product");
  const [shippingDraft, setShippingDraft] = useState<{ status: ShippingStatus; trackingNumber: string; carrier: string; note: string }>({
    status: "payment_pending",
    trackingNumber: "",
    carrier: "",
    note: "",
  });

  const syncDraft = useMemo(() => {
    if (!item?.shipping) return null;
    return {
      status: item.shipping.status,
      trackingNumber: item.shipping.trackingNumber ?? "",
      carrier: item.shipping.carrier ?? "",
      note: item.shipping.note ?? "",
    };
  }, [item?.shipping]);

  useEffect(() => {
    if (!syncDraft) return;
    setShippingDraft(syncDraft);
  }, [syncDraft]);

  const refreshAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-order-detail", id] }),
      queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] }),
    ]);
  };

  const paidMutation = useMutation({ mutationFn: markPaid, onSuccess: refreshAll });
  const cancelMutation = useMutation({ mutationFn: cancelOrder, onSuccess: refreshAll });
  const deleteMutation = useMutation({
    mutationFn: deleteOrder,
    onSuccess: async () => {
      await refreshAll();
      navigate("/admin/numeroloji/siparisler", { replace: true });
    },
  });
  const shippingMutation = useMutation({ mutationFn: updateShipping, onSuccess: refreshAll });

  if (!configured) {
    return <div className="mx-auto max-w-2xl p-6"><Card className="p-5">Admin ayarları yapılmamış.</Card></div>;
  }

  if (!authenticated) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Sipariş detayı</div>
          <Button variant="outline" className="rounded-full" size="sm" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>Çıkış</Button>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">#{id}</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Siparişi tüm senaryolarıyla yönetebilirsiniz.</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" className="rounded-full"><Link to="/admin/numeroloji/siparisler">Siparişlere dön</Link></Button>
                {item?.status === "pending" ? <Button className="rounded-full" onClick={() => paidMutation.mutate(id)} disabled={paidMutation.isPending}>Ödendi işaretle</Button> : null}
                {item?.status !== "cancelled" ? <Button variant="outline" className="rounded-full border-rose-500/30 text-rose-700 dark:text-rose-200" onClick={() => cancelMutation.mutate(id)} disabled={cancelMutation.isPending}>İptal et</Button> : null}
                <Button variant="outline" className="rounded-full" onClick={() => deleteMutation.mutate(id)} disabled={deleteMutation.isPending}>Sil</Button>
              </div>
            </div>

            {detailQuery.isLoading ? <Card className="p-5">Yükleniyor...</Card> : null}
            {detailQuery.isError ? <Card className="p-5 text-rose-600">{(detailQuery.error as Error).message}</Card> : null}

            {item ? (
              <div className="grid gap-6 lg:grid-cols-3">
                <Card className="p-5 lg:col-span-2">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">Sipariş özeti</div>
                  <div className="mt-4 grid gap-3 text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                    <div><span className="font-semibold">Durum:</span> {item.shipping?.status ?? item.status}</div>
                    <div><span className="font-semibold">Tutar:</span> {formatTry(item.totalTry)}</div>
                    <div><span className="font-semibold">Oluşturma:</span> {formatDate(item.createdAt)}</div>
                    <div><span className="font-semibold">Ödeme:</span> {formatDate(item.paidAt)}</div>
                    <div><span className="font-semibold">Ödeme sonucu:</span> {item.payment?.lastStatus ?? "—"}</div>
                    <div><span className="font-semibold">Son kontrol:</span> {formatDate(item.payment?.lastCheckedAt)}</div>
                  </div>

                  <div className="mt-6 text-sm font-semibold text-slate-900 dark:text-slate-50">Ürünler</div>
                  <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-900">
                        <tr>
                          <th className="px-4 py-3">Ürün</th>
                          <th className="px-4 py-3">Adet</th>
                          <th className="px-4 py-3">Birim</th>
                          <th className="px-4 py-3">Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.items.map((entry, index) => (
                          <tr key={`${entry.kind}-${index}`} className="border-t border-slate-200 dark:border-slate-800">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900 dark:text-slate-50">{entry.title}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">{entry.kind === "package" ? entry.packageKey : entry.sku || entry.productId}</div>
                            </td>
                            <td className="px-4 py-3">{entry.qty}</td>
                            <td className="px-4 py-3">{formatTry(entry.unitPriceTry)}</td>
                            <td className="px-4 py-3 font-semibold">{formatTry(entry.lineTotalTry)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-5">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">Müşteri / Fatura</div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                    <div><span className="font-semibold">Ad Soyad:</span> {item.customer.firstName} {item.customer.lastName}</div>
                    <div><span className="font-semibold">E-posta:</span> {item.customer.email}</div>
                    <div><span className="font-semibold">Telefon:</span> {item.customer.phone || "—"}</div>
                    <div><span className="font-semibold">TCKN:</span> {item.customer.identityNumber || "—"}</div>
                    <div><span className="font-semibold">Fatura adresi:</span> {formatAddress(item.customer.billingAddress ?? item.customer)}</div>
                    <div><span className="font-semibold">Teslimat adresi:</span> {formatAddress(item.customer.shippingAddress ?? item.customer)}</div>
                  </div>
                </Card>

                <Card className="p-5 lg:col-span-2">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">İlişkili istekler</div>
                  <div className="mt-3 space-y-2">
                    {(detailQuery.data?.requests ?? []).length ? detailQuery.data?.requests.map((request) => (
                      <div key={request.id} className="rounded-xl border border-slate-200 px-4 py-3 text-sm dark:border-slate-800">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Link className="font-medium text-slate-900 underline-offset-2 hover:underline dark:text-slate-50" to={`/admin/numeroloji/${encodeURIComponent(request.id)}`}>{request.id}</Link>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{request.status} • {formatDate(request.createdAt)}</div>
                        </div>
                      </div>
                    )) : <div className="text-sm text-slate-500 dark:text-slate-400">Bu siparişe bağlı istek yok.</div>}
                  </div>
                </Card>

                <Card className="p-5">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">Kargo yönetimi</div>
                  {hasPhysicalItem ? (
                    <div className="mt-4 space-y-3">
                      <select
                        value={shippingDraft.status}
                        onChange={(e) => setShippingDraft((prev) => ({ ...prev, status: e.target.value as ShippingStatus }))}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                      >
                        <option value="payment_pending">Ödeme bekleniyor</option>
                        <option value="preparing">Hazırlanıyor</option>
                        <option value="shipped">Kargoya verildi</option>
                        <option value="delivered">Teslim edildi</option>
                        <option value="cancelled">İptal</option>
                      </select>
                      <Input value={shippingDraft.carrier} onChange={(e) => setShippingDraft((prev) => ({ ...prev, carrier: e.target.value }))} placeholder="Kargo firması" />
                      <Input value={shippingDraft.trackingNumber} onChange={(e) => setShippingDraft((prev) => ({ ...prev, trackingNumber: e.target.value }))} placeholder="Takip numarası" />
                      <Input value={shippingDraft.note} onChange={(e) => setShippingDraft((prev) => ({ ...prev, note: e.target.value }))} placeholder="Kargo notu" />
                      <div className="text-xs text-slate-500 dark:text-slate-400">Son güncelleme: {formatDate(item.shipping?.updatedAt)}</div>
                      <Button
                        className="w-full rounded-full"
                        onClick={() => shippingMutation.mutate({ id, status: shippingDraft.status, carrier: shippingDraft.carrier || undefined, trackingNumber: shippingDraft.trackingNumber || undefined, note: shippingDraft.note || undefined })}
                        disabled={shippingMutation.isPending}
                      >
                        Kargo durumunu kaydet
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">Bu siparişte fiziksel ürün yok.</div>
                  )}
                </Card>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
